import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FaPhoneAlt, FaEnvelope, FaLifeRing, FaLock, FaSearch, FaTimes } from 'react-icons/fa';

const Header = () => {
  const [isSticky, setIsSticky] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchBox, setShowSearchBox] = useState(false);
  const location = useLocation();

  // Scroll to top of the page whenever route changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const getNavLinkClass = ({ isActive }) => {
    return isActive ? "nav-link text-teal fw-bold active-menu-item" : "nav-link text-white";
  };

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    alert(`Logging in with Username: ${username}`);
    setShowLoginModal(false);
    setUsername('');
    setPassword('');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      alert(`Searching for: ${searchQuery}`);
      // Add your search routing or filtering logic here
      setSearchQuery('');
      setShowSearchBox(false);
    }
  };

  return (
    <>
      <header className={`site-header ${isSticky ? 'sticky-header-active shadow-sm' : ''}`}>
        {/* Top Bar */}
        <div className="top-bar text-light py-2">
          <div className="container d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
            <div className="d-flex flex-wrap justify-content-center align-items-center gap-4">
              <span><FaPhoneAlt className="me-2"/>Call: <strong>+91 889 786 6074</strong></span>
              <span><FaEnvelope className="me-2"/>E-mail: <strong>info@lotoswealth.com</strong></span>
            </div>
            <div className="d-flex flex-wrap justify-content-center align-items-center gap-3">
              <NavLink to="/support" className="text-light text-decoration-none"><FaLifeRing className="me-1"/> SUPPORT</NavLink>
              <button 
                onClick={() => setShowLoginModal(true)} 
                className="btn btn-link text-light text-decoration-none p-0 bg-transparent border-0"
                style={{ fontSize: 'inherit', fontWeight: 'inherit' }}
              >
                <FaLock className="me-1"/> LOGIN
              </button>
              <button 
                onClick={() => setShowSearchBox(!showSearchBox)} 
                className="btn btn-link text-light text-decoration-none p-0 bg-transparent border-0 d-none d-md-block ms-2"
                style={{ fontSize: 'inherit', fontWeight: 'inherit' }}
                aria-label="Toggle Search"
              >
                <FaSearch />
              </button>
            </div>
          </div>
        </div>

        {/* Collapsible Search Bar Input Section */}
        {showSearchBox && (
          <div className="bg-dark py-2 border-top border-secondary">
            <div className="container" style={{ maxWidth: '600px' }}>
              <form onSubmit={handleSearchSubmit} className="input-group">
                <input 
                  type="text" 
                  className="form-control form-control-sm" 
                  placeholder="Search wealth management, loans, articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  required
                />
                <button 
                  type="submit" 
                  className="btn btn-custom-teal btn-sm text-white fw-bold px-3"
                >
                  Search
                </button>
                <button 
                  type="button" 
                  className="btn btn-outline-secondary btn-sm text-white px-2"
                  onClick={() => setShowSearchBox(false)}
                >
                  <FaTimes />
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Main Navbar */}
        <nav className="navbar navbar-expand-lg navbar-dark main-nav position-relative">
          <div className="container">
            <NavLink className="navbar-brand text-white fw-bold fs-3" to="/"> 
              <img src="/images/lotoswealth-logo.png" alt="Lotoswealth Logo" width={280} /> 
            </NavLink>
            
            <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarSupportedContent">
              <span className="navbar-toggler-icon"></span>
            </button>
            
            <div className="collapse navbar-collapse" id="navbarSupportedContent">
              <ul className="navbar-nav m-auto mb-2 mb-lg-0 ps-4">
                <li className="nav-item">
                  <NavLink className={getNavLinkClass} to="/" end>Home</NavLink>
                </li>
                <li className="nav-item">
                  <NavLink className={getNavLinkClass} to="/about-us">About Us</NavLink>
                </li>

                {/* --- Services Mega Menu --- */}
                <li className="nav-item dropdown position-relative dropdown-hover">
                  <a className="nav-link dropdown-toggle text-white" href="#services" id="servicesMegaMenu" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                      Services
                  </a>
                  <div className="dropdown-menu shadow border-0 p-4 mega-menu-box" aria-labelledby="servicesMegaMenu">
                    <div className="container px-0">
                      <div className="row gx-4">
                        <div className="col-lg-4 col-md-12 mb-3 mb-lg-0">
                          <div className="tp-megamenu-list">
                            <h6 className="dropdown-header text-dark fw-bold ps-0 mb-2 border-bottom pb-2">Wealth Management</h6>
                            <ul className="list-unstyled">
                              <li className="mb-2"><NavLink to="/wealth-management" className="dropdown-item p-0 bg-transparent">Wealth Management</NavLink></li>
                              <li className="mb-2"><NavLink to="/portfolio-management-services" className="dropdown-item p-0 bg-transparent text-wrap">Portfolio Management Services</NavLink></li>                                         
                              <li className="mb-2"><NavLink to="/tax-planning" className="dropdown-item p-0 bg-transparent">Tax Planning</NavLink></li>
                              <li className="mb-2"><NavLink to="/offshore-products" className="dropdown-item p-0 bg-transparent">Offshore Products</NavLink></li>
                              <li className="mb-2"><NavLink to="/estate-planning" className="dropdown-item p-0 bg-transparent">Estate Planning</NavLink></li>
                              <li className="mb-2"><NavLink to="/alternate-investments" className="dropdown-item p-0 bg-transparent">Alternate Investments</NavLink></li>
                              <li className="mb-2"><NavLink to="/private-equity" className="dropdown-item p-0 bg-transparent">Private Equity</NavLink></li>
                              <li className="mb-2"><NavLink to="/private-credit" className="dropdown-item p-0 bg-transparent">Private Credit</NavLink></li>
                            </ul>
                          </div>
                        </div>
                        <div className="col-lg-4 col-md-12 mb-3 mb-lg-0">
                          <div className="tp-megamenu-list">
                            <h6 className="dropdown-header text-dark fw-bold ps-0 mb-2 border-bottom pb-2">Investment Planning</h6>
                            <ul className="list-unstyled">
                              <li className="mb-2"><NavLink to="/financial-planning" className="dropdown-item p-0 bg-transparent">Financial Planning</NavLink></li>
                              <li className="mb-2"><NavLink to="/goal-based-investments" className="dropdown-item p-0 bg-transparent">Goal Based Investments</NavLink></li>
                              <li className="mb-2"><NavLink to="/retirement-planning" className="dropdown-item p-0 bg-transparent">Retirement Planning</NavLink></li>
                              <li className="mb-2"><NavLink to="/post-retirement-solutions" className="dropdown-item p-0 bg-transparent">Post Retirement Solutions</NavLink></li>
                              <li className="mb-2"><NavLink to="/fire-planning" className="dropdown-item p-0 bg-transparent">FIRE Planning</NavLink></li>
                              <li className="mb-2"><NavLink to="/corporate-financial-wellness" className="dropdown-item p-0 bg-transparent text-wrap">Corporate Financial Wellness</NavLink></li>
                            </ul>
                          </div>
                        </div>
                        <div className="col-lg-4 col-md-12">
                          <div className="tp-megamenu-list">
                            <h6 className="dropdown-header text-dark fw-bold ps-0 mb-2 border-bottom pb-2">Asset Management</h6>
                            <ul className="list-unstyled">
                              <li className="mb-2"><NavLink to="/asset-management" className="dropdown-item p-0 bg-transparent">Asset Management</NavLink></li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>

                {/* --- Investment Dropdown --- */}
                <li className="nav-item dropdown dropdown-hover">
                  <a className="nav-link dropdown-toggle text-white" href="#investment" id="navbarDropdown2" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                      Investment
                  </a>
                  <ul className="dropdown-menu border-0 shadow-sm" aria-labelledby="navbarDropdown2">
                    <li><NavLink className="dropdown-item py-2" to="/stock-investment">Stocks Investment</NavLink></li>
                    <li><NavLink className="dropdown-item py-2" to="/mutual-funds-investment">Mutual Funds Investment</NavLink></li>
                    <li><NavLink className="dropdown-item py-2" to="/portfolio-management-services">Portfolio Management Services (PMS)</NavLink></li>
                  </ul>
                </li>

                {/* --- Loans Dropdown --- */}
                <li className="nav-item dropdown dropdown-hover">
                  <a className="nav-link dropdown-toggle text-white" href="#loans" id="navbarDropdown3" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                      Loans
                  </a>
                  <ul className="dropdown-menu border-0 shadow-sm" aria-labelledby="navbarDropdown3">
                    <li><NavLink className="dropdown-item py-2" to="/home-loan">Home Loan</NavLink></li>
                    <li><NavLink className="dropdown-item py-2" to="/car-loan">Car Loan</NavLink></li>
                    <li><NavLink className="dropdown-item py-2" to="/education-loan">Education Loan</NavLink></li>
                  </ul>
                </li>

                {/* --- Resources Dropdown --- */}
                <li className="nav-item dropdown dropdown-hover">
                  <a className="nav-link dropdown-toggle text-white" href="#resources" id="navbarDropdown4" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                      Resources
                  </a>
                  <ul className="dropdown-menu border-0 shadow-sm" aria-labelledby="navbarDropdown4">
                    <li><NavLink className="dropdown-item py-2" to="/articles">Articles</NavLink></li>
                    <li><NavLink className="dropdown-item py-2" to="/video-clips">Video Clips</NavLink></li>
                    <li><NavLink className="dropdown-item py-2" to="/image-gallery">Image Gallery</NavLink></li>
                  </ul>
                </li>

                <li className="nav-item">
                  <NavLink className={getNavLinkClass} to="/contact-us">Contact Us</NavLink>
                </li>

                <li className="nav-item ms-lg-3 mt-2 mt-lg-0">
                  <NavLink to="/portfolio-management-services" className="text-decoration-none">
                    <button className="btn-free-quote w-100">  P M S <span className="ms-2">→</span></button>
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>
        </nav>
      </header>

      {/* --- Login Modal Popup --- */}
      {showLoginModal && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" 
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 1050 }}
        >
          <div 
            className="bg-white p-4 rounded-4 shadow-lg position-relative mx-3 w-100" 
            style={{ maxWidth: '400px' }}
          >
            <button 
              type="button"
              onClick={() => setShowLoginModal(false)}
              className="position-absolute top-0 end-0 m-3 bg-transparent border-0 text-secondary fs-5 d-flex align-items-center justify-content-center"
              style={{ cursor: 'pointer', width: '32px', height: '32px' }}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            <div className="text-center mb-4 pt-2">
              <h3 className="fw-bold text-dark">Client Login</h3>
              <p className="text-muted small">Access your Lotoswealth portfolio and account</p>
            </div>

            <form onSubmit={handleLoginSubmit}>
              <div className="mb-3 text-start">
                <label className="form-label fw-semibold small text-secondary">Username or Email</label>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required 
                />
              </div>

              <div className="mb-4 text-start">
                <label className="form-label fw-semibold small text-secondary">Password</label>
                <input 
                  type="password" 
                  className="form-control" 
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required 
                />
              </div>

              <button 
                type="submit" 
                className="btn-custom-teal w-100 py-2 fw-bold text-white border-0 rounded"
              >
                Login
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;